// components/socials/profile/Invitation.jsx
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import firebase from "firebase/app";
import "firebase/firestore";

import { db } from "../../../firebase/firebase.utils";

export default function Invitation() {
  const { inviteId: routeInviteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.currentUser);
  const initialInvite = location.state?.invite || null;

  const [invite, setInvite] = useState(initialInvite);
  const [loading, setLoading] = useState(!initialInvite);
  const [busy, setBusy] = useState(false);
  const inviteId =
    routeInviteId || initialInvite?.inviteId || initialInvite?.id;

  useEffect(() => {
    if (!inviteId) {
      setLoading(false);
      return undefined;
    }

    return db
      .collection("invites")
      .doc(inviteId)
      .onSnapshot(
        (snapshot) => {
          setInvite(
            snapshot.exists
              ? { id: snapshot.id, inviteId: snapshot.id, ...snapshot.data() }
              : null,
          );
          setLoading(false);
        },
        (error) => {
          console.log("Invitation load error:", error);
          setInvite(null);
          setLoading(false);
        },
      );
  }, [inviteId]);

  const run = async (action) => {
    if (!currentUser?.uid || !inviteId || busy) return;
    try {
      setBusy(true);
      await action();
    } catch (error) {
      console.log("Invitation action error:", error);
      window.alert("The invitation could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  const accept = () =>
    run(async () => {
      await db.collection("invites").doc(inviteId).update({
        status: "accepted",
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      if (invite.targetType === "station") {
        navigate(`/s/${invite.spaceId}/e/${invite.stationId}`, {
          replace: true,
        });
      } else if (invite.targetType === "space") {
        navigate(`/s/${invite.spaceId}`, { replace: true });
      } else {
        navigate(-1);
      }
    });

  const decline = () =>
    run(async () => {
      await db.collection("invites").doc(inviteId).update({
        status: "declined",
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      navigate(-1);
    });

  const blockInviter = () => {
    if (
      !invite?.inviterUid ||
      !window.confirm("Block this inviter and decline the invitation?")
    )
      return;

    run(async () => {
      const batch = db.batch();
      batch.set(
        db.collection("users").doc(currentUser.uid),
        {
          blockedUsers: firebase.firestore.FieldValue.arrayUnion(
            String(invite.inviterUid),
          ),
        },
        { merge: true },
      );
      batch.update(db.collection("invites").doc(inviteId), {
        status: "declined",
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      await batch.commit();
      navigate(-1);
    });
  };

  if (loading) {
    return (
      <main className="social-route-page social-route-page--center">
        Loading invitation…
      </main>
    );
  }

  const targetType = invite?.targetType;
  const title =
    targetType === "space"
      ? "Workspace invitation"
      : targetType === "station"
        ? "Project invitation"
        : "Invitation";

  return (
    <main className="social-route-page invitation-page">
      <section className="invitation-card">
        {!invite ? (
          <>
            <h1>Invitation not found</h1>
            <p>
              This invitation may have been removed or the link is incomplete.
            </p>
          </>
        ) : (
          <>
            <span
              className={`invitation-status invitation-status--${invite.status || "pending"}`}
            >
              {invite.status || "pending"}
            </span>
            <h1>{title}</h1>
            <p>
              {targetType === "station"
                ? "You’re invited to join a project."
                : "You’re invited to join a workspace."}
            </p>
            {invite.inviterName && (
              <dl>
                <dt>From</dt>
                <dd>{invite.inviterName}</dd>
              </dl>
            )}
            {invite.spaceName && (
              <dl>
                <dt>Workspace</dt>
                <dd>{invite.spaceName}</dd>
              </dl>
            )}
            {invite.stationName && (
              <dl>
                <dt>Project</dt>
                <dd>{invite.stationName}</dd>
              </dl>
            )}
            {!invite.inviterName && invite.inviterUid && (
              <dl>
                <dt>From</dt>
                <dd>{invite.inviterUid}</dd>
              </dl>
            )}

            {(invite.status || "pending") === "pending" && (
              <div className="invitation-card__actions">
                <button type="button" disabled={busy} onClick={decline}>
                  Decline
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={accept}
                >
                  {busy ? "Updating…" : "Accept"}
                </button>
                {invite.inviterUid && (
                  <button
                    type="button"
                    className="danger"
                    disabled={busy}
                    onClick={blockInviter}
                  >
                    Block
                  </button>
                )}
              </div>
            )}
          </>
        )}
        <button
          type="button"
          className="text-button"
          onClick={() => navigate(-1)}
        >
          Close
        </button>
      </section>
    </main>
  );
}
